import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { customerAPI, userAPI, locationAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import {
  Button,
  Card,
  StatsCard,
  Modal,
  Input,
  Select,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
  Badge,
} from '../../UI';
import { CheckCircle2, XCircle, AlertCircle, Clock, User, ChevronRight } from 'lucide-react';
import BirthdayWidget from '../BirthdayWidget';

const AdminOverview = () => {
  const { user } = useAuth();
  
  // API hooks
  const { data: userStats, loading: userStatsLoading } = useApi(userAPI.getStats, []);
  const { data: customerStats, loading: customerStatsLoading } = useApi(customerAPI.getStats, []);
  const { data: locationStats, loading: locationStatsLoading } = useApi(locationAPI.getStats, []);
  const { data: usersData, loading: usersLoading } = useApi(userAPI.getUsers, []);
  const { data: locationsData, loading: locationsLoading } = useApi(locationAPI.getLocations, []);
  const { data: pendingData, loading: pendingLoading, refetch: refetchPending } = useApi(userAPI.getPendingPersonalDetails, []);
  
  const { loading: exportLoading, execute: executeExport } = useAsyncAction();
  const { loading: approvalLoading, execute: executeApproval } = useAsyncAction();
  
  const users = usersData?.users || [];
  const locations = locationsData?.locations || [];
  const pendingApprovals = pendingData?.data || pendingData || [];
  
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Export to Excel
  const handleExportExcel = async () => {
    try {
      const result = await executeExport(customerAPI.exportToExcel);
      if (result.success && result.data.url) {
        window.open(result.data.url, '_blank');
        setSuccessMessage('Export started successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      setError('Failed to export data');
    }
  };

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {successMessage && <SuccessMessage message={successMessage} onClose={() => setSuccessMessage('')} />}

      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">Welcome back, {user?.name}!</h2>
        <p className="text-blue-100">Here's what's happening with your organization today.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Employees"
          value={userStatsLoading ? '...' : userStats?.total || 0}
          icon="👥"
          trend={{ value: userStats?.activeCount || 0, label: 'active', direction: 'up' }}
          loading={userStatsLoading}
        />
        <StatsCard
          title="Total Customers"
          value={customerStatsLoading ? '...' : customerStats?.total || 0}
          icon="🏢"
          trend={{ value: customerStats?.thisMonth || 0, label: 'this month', direction: 'up' }}
          loading={customerStatsLoading}
        />
        <StatsCard
          title="Locations"
          value={locationStatsLoading ? '...' : locationStats?.total || 0}
          icon="📍"
          trend={{ value: locationStats?.activeCount || 0, label: 'active', direction: 'up' }}
          loading={locationStatsLoading}
        />
        <StatsCard
          title="Active Today"
          value={userStatsLoading ? '...' : userStats?.activeToday || 0}
          icon="⚡"
          loading={userStatsLoading}
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a href="/dashboard/employees" className="flex flex-col items-center p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
            <span className="text-2xl mb-2">👤</span>
            <span className="text-sm font-medium text-blue-700">Add Employee</span>
          </a>
          <a href="/dashboard/locations" className="flex flex-col items-center p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
            <span className="text-2xl mb-2">📍</span>
            <span className="text-sm font-medium text-green-700">Add Location</span>
          </a>
          <a href="/dashboard/leaves" className="flex flex-col items-center p-4 bg-yellow-50 rounded-xl hover:bg-yellow-100 transition-colors">
            <span className="text-2xl mb-2">📅</span>
            <span className="text-sm font-medium text-yellow-700">Manage Leaves</span>
          </a>
          <a href="/dashboard/payroll" className="flex flex-col items-center p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
            <span className="text-2xl mb-2">💰</span>
            <span className="text-sm font-medium text-purple-700">Payroll</span>
          </a>
        </div>
      </div>

      {/* Pending Personal Detail Approvals */}
      {pendingApprovals.length > 0 && (
        <div className="bg-white rounded-xl border-2 border-amber-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-amber-200 bg-amber-50 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-200 flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-amber-700" />
              </div>
              <h3 className="font-semibold text-amber-800">Pending Personal Details Approvals</h3>
              <Badge className="bg-amber-200 text-amber-800 ml-1">{pendingApprovals.length}</Badge>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {pendingApprovals.map(emp => (
              <div key={emp._id} className="px-6 py-4 flex items-center justify-between hover:bg-amber-50/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-medium">
                    {emp.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{emp.name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      {emp.employeeId && <span>{emp.employeeId}</span>}
                      {emp.department && <span>• {emp.department}</span>}
                      {emp.pendingPersonal?.submittedAt && (
                        <span>• Submitted {new Date(emp.pendingPersonal.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                      )}
                    </div>
                  </div>
                </div>
                <a
                  href={`/dashboard/employees/${emp._id}`}
                  className="flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-800 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Review <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Employees */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Recent Employees</h3>
            <a href="/dashboard/employees" className="text-sm text-blue-600 hover:underline">View all</a>
          </div>
          {usersLoading ? (
            <div className="p-6 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : users.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {users.slice(0, 5).map(emp => (
                <div key={emp._id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium">
                      {emp.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{emp.name}</p>
                      <p className="text-sm text-gray-500">{emp.email || emp.username}</p>
                    </div>
                  </div>
                  <Badge variant={emp.active ? 'success' : 'secondary'}>
                    {emp.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">No employees found</div>
          )}
        </div>

        {/* Recent Locations */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Recent Locations</h3>
            <a href="/dashboard/locations" className="text-sm text-blue-600 hover:underline">View all</a>
          </div>
          {locationsLoading ? (
            <div className="p-6 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : locations.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {locations.slice(0, 5).map(loc => (
                <div key={loc._id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <span className="text-green-600">📍</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{loc.name}</p>
                      <p className="text-sm text-gray-500">{loc.mandal || loc.address?.city || 'N/A'}</p>
                    </div>
                  </div>
                  <Badge variant={loc.active !== false ? 'success' : 'secondary'}>
                    {loc.active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">No locations found</div>
          )}
        </div>

        {/* Birthday Widget */}
        <div className="lg:col-span-1">
          <BirthdayWidget />
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
